<template>
  <div>
    <b-message v-if="messageString" type="is-danger" aria-close-label="Close message">
      {{ messageString }}
    </b-message>
    <ValidationObserver ref="observer">
      <b-field v-if="withOldPwd">
        <InputWithValidation
          v-model="oldpassword"
          :rules="!sent ? 'required' : ''"
          type="password"
          password-reveal
          :label="$t('input-old-password')"
          :placeholder="$t('profilePage.ancien-mot-de-passe-placeholder')"
          vid="oldpassword"
        />
      </b-field>
      <b-field>
        <PasswordCreationWithValidation
          v-model="password"
          :label="$t('profilePage.nouveau-mot-de-passe')"
          :placeholder="$t('profilePage.nouveau-mot-de-passe-placeholder')"
          :validation-activated="!sent"
        />
      </b-field>
      <b-field>
        <InputWithValidation
          v-model="confirmation"
          :rules="!sent ? 'required|confirmed:password' : ''"
          type="password"
          password-reveal
          :label="$t('profilePage.mot-de-passe-confirmation')"
          :placeholder="$t('profilePage.confirmation-mot-de-passe-placeholder')"
        />
      </b-field>
    </ValidationObserver>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import InputWithValidation from "@/views/inputs/InputWithValidation.vue"
import PasswordCreationWithValidation from "@/views/inputs/PasswordCreationWithValidation.vue"
import ValidationObserver from "@/services/validation/ValidationObserver.vue"
import { LoginService } from "@/services/rest/login/LoginService"
import { PasswordCheckDTO, PasswordResetDTO, UtilisateurDTO } from "@/model/bean/GeneratedDTOs"
import { BMessage, BField } from "buefy"

export default defineComponent({
  name: "PasswordReset",
  components: {
    ValidationObserver,
    InputWithValidation,
    PasswordCreationWithValidation,
    BField,
    BMessage,
  },
  props: {
    user: {
      type: Object as PropType<UtilisateurDTO>,
      required: true,
    },
    withOldPwd: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      loginService: LoginService.INSTANCE,
      oldpassword: "",
      password: "",
      confirmation: "",
      messageString: "",
      sent: false,
    }
  },
  watch: {
    oldpassword(): void {
      this.typedPassword()
    },
    password(): void {
      this.typedPassword()
    },
    confirmation(): void {
      this.typedPassword()
    },
  },
  methods: {
    newPassword(newPassword: string): void {
      this.sent = false
      this.password = newPassword
    },
    // Used by ProfileModale
    async submitPassword(): Promise<string> {
      // If user has started writing something about a password change, we check if she's filled everything
      if (this.oldpassword || this.password || this.confirmation) {
        const observerComponent = this.$refs["observer"]
        //@ts-ignore
        const validationResult = await observerComponent.validateWithInfo()
        const errors = Object.values(validationResult.errors).flat()

        if (errors.length !== 0) {
          this.messageString = this.$t("profilePage.missing-fields").toString()
          return this.messageString
        } else if (!this.withOldPwd) {
          return this.resetPassword()
        } else if (this.oldpassword) {
          const oldPasswordCheck = new PasswordCheckDTO()
          oldPasswordCheck.motDePasse = this.oldpassword
          // Reset the users password
          return this.loginService.checkPassword(oldPasswordCheck).then(
            (_success) => {
              return this.resetPassword()
            },
            (_reject) => {
              return this.$t("profilePage.authentication-error").toString()
            },
          )
        }
      }

      return ""
    },
    resetPassword(): Promise<string> {
      const passwordReset = new PasswordResetDTO()
      passwordReset.motDePasse = this.password
      passwordReset.confirmationMotDePasse = this.confirmation
      return this.loginService.changePassword(passwordReset, this.user).then(
        (_success) => {
          this.oldpassword = ""
          this.password = ""
          this.confirmation = ""
          this.messageString = ""

          this.$nextTick(() => {
            this.sent = true
          })
          return ""
        },
        (_reject) => {
          this.messageString = this.$t("profilePage.password-error").toString()
          return this.messageString
        },
      )
    },
    typedPassword(): void {
      this.sent = false
    },
  },
})
</script>
